import { supabase } from './supabase';
import type { Modifier } from '@/store/useCartStore';
import {
  getPendingOrders,
  removePendingOrder,
  incrementRetryCount,
  addPendingOrder,
} from './db';
import { toast } from 'sonner';
import { MAX_RETRY_ATTEMPTS, SYNC_ORDER_DELAY } from '@/config/cache.config';
import { logDebug } from '@/lib/logger';
import { devLog } from '@/lib/utils';

// Estado de sincronización
let isSyncing = false;
let syncInterval: NodeJS.Timeout | null = null;

/**
 * Iniciar sincronización automática
 */
export function startAutoSync(intervalMs: number = 30000) {
  if (syncInterval) return;

  if (import.meta.env.DEV) logDebug('Auto-sync iniciado');

  // Intentar sincronizar inmediatamente
  syncPendingOrders();

  // Luego cada X segundos
  syncInterval = setInterval(() => {
    syncPendingOrders();
  }, intervalMs);
}

/**
 * Detener sincronización automática
 */
export function stopAutoSync() {
  if (syncInterval) {
    clearInterval(syncInterval);
    syncInterval = null;
    if (import.meta.env.DEV) logDebug('Auto-sync detenido');
  }
}

/**
 * Sincronizar órdenes pendientes
 */
export async function syncPendingOrders(): Promise<{
  success: number;
  failed: number;
  total: number;
}> {
  if (isSyncing) {
    devLog.warn('⏳ Ya hay una sincronización en progreso...');
    return { success: 0, failed: 0, total: 0 };
  }

  if (!navigator.onLine) {
    devLog.warn('📡 Sin conexión, saltando sincronización');
    return { success: 0, failed: 0, total: 0 };
  }

  isSyncing = true;

  try {
    const pendingOrders = await getPendingOrders();

    if (pendingOrders.length === 0) {
      return { success: 0, failed: 0, total: 0 };
    }

    devLog.warn(`🔄 Sincronizando ${pendingOrders.length} órdenes pendientes...`);

    let successCount = 0;
    let failedCount = 0;

    for (const pending of pendingOrders) {
      try {
        // Verificar si excedió intentos máximos
        if (Number(pending.retry_count) >= MAX_RETRY_ATTEMPTS) {
          devLog.error(`❌ Orden ${pending.id} excedió intentos máximos, descartando`);
          await removePendingOrder(pending.id);
          failedCount++;
          continue;
        }

        // Intentar crear la orden en Supabase
        const result = await createOrderInSupabase(pending.order_data as SyncOrderData);

        if (result.success) {
          await removePendingOrder(pending.id);
          successCount++;
          devLog.warn(`✅ Orden ${pending.id} sincronizada exitosamente`);
        } else {
          await incrementRetryCount(pending.id);
          failedCount++;
          devLog.warn(`⚠️ Orden ${pending.id} falló, reintentando más tarde`);
        }
      } catch (error) {
        devLog.error(`❌ Error al sincronizar orden ${pending.id}:`, error);
        await incrementRetryCount(pending.id);
        failedCount++;
      }

      // Pequeño delay entre órdenes para no saturar
      await new Promise(resolve => setTimeout(resolve, SYNC_ORDER_DELAY));
    }

    if (successCount > 0) {
      toast.success(`✅ ${successCount} órdenes sincronizadas`, {
        description: failedCount > 0 ? `${failedCount} fallaron` : undefined,
      });
    }

    return {
      success: successCount,
      failed: failedCount,
      total: pendingOrders.length,
    };
  } catch (error) {
    devLog.error('❌ Error en sincronización:', error);
    return { success: 0, failed: 0, total: 0 };
  } finally {
    isSyncing = false;
  }
}

/**
 * Crear orden en Supabase (lógica duplicada de useCheckout)
 */
// Describe lo que el POS realmente encola (useCheckout): los campos de cliente/mesa
// llegan como null cuando no aplican y createOrderInSupabase ya los normaliza con `|| null`.
interface SyncOrderData {
  tenantId: string;
  branchId: string;
  items: Array<{
    product_id: string;
    product_name: string;
    quantity: number;
    unit_price: number;
    subtotal: number;
    /** Snapshot de modificadores del carrito — array, va directo al JSONB de la RPC. */
    modifiers?: Modifier[] | null;
    notes?: string | null;
    stock?: number;
  }>;
  total: number;
  subtotal: number;
  tax: number;
  paymentMethod: string;
  waiterName?: string;
  tableNumber?: string | null;
  customerName?: string | null;
  customerPhone?: string | null;
  courier_service_id?: string | null;
  order_type?: string;
}

async function createOrderInSupabase(orderData: SyncOrderData): Promise<{ success: boolean }> {
  try {
    const { tenantId, branchId, items, total, subtotal, tax, paymentMethod, waiterName, tableNumber, customerName, customerPhone, courier_service_id } = orderData;
    const orderType = orderData.order_type || 'pos';

    // Crear orden + ítems ATÓMICAMENTE vía RPC (dominio único orders_v2);
    // reemplaza insert a la vista `orders` + `order_items` + rollback manual.
    const { error: orderError } = await supabase.rpc('fn_create_pos_order', {
      p: {
        tenant_id: tenantId,
        branch_id: branchId,
        order_type: orderType,
        table_number: tableNumber || null,
        waiter_name: waiterName,
        customer_name: customerName || null,
        customer_phone: customerPhone || null,
        subtotal,
        tax,
        total,
        payment_method: paymentMethod,
        payment_status: 'paid',
        status: 'pending',
        ...(courier_service_id != null && { courier_service_id }),
        items: items.map((item) => ({
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          subtotal: item.subtotal,
          modifiers: item.modifiers || null,
          notes: item.notes || null,
        })),
      },
    });

    if (orderError) {
      devLog.error('❌ Error al crear orden:', orderError);
      return { success: false };
    }

    // El stock lo descuenta la BASE (trigger sobre el pedido), no esta cola. Antes se descontaba
    // acá con un read-then-write sobre un `stock` que la cola traía CACHEADO desde el momento en que
    // se hizo la venta offline: al sincronizar, ese número ya era viejo y pisaba el stock real.

    return { success: true };
  } catch (error) {
    devLog.error('❌ Error general en createOrderInSupabase:', error);
    return { success: false };
  }
}

/**
 * Agregar orden a la cola offline
 */
export async function queueOrderForSync(orderData: SyncOrderData): Promise<string> {
  const id = await addPendingOrder(orderData);
  devLog.warn('📝 Orden agregada a cola offline:', id);

  // Intentar sincronizar inmediatamente si hay conexión
  if (navigator.onLine) {
    setTimeout(() => syncPendingOrders(), 1000);
  }

  return id;
}

/**
 * Detectar cambios en el estado de conexión
 */
export function setupConnectionListeners() {
  window.addEventListener('online', () => {
    devLog.warn('✅ Conexión restaurada');
    toast.success('Conexión restaurada', {
      description: 'Sincronizando órdenes pendientes...',
    });
    syncPendingOrders();
  });

  window.addEventListener('offline', () => {
    devLog.warn('❌ Conexión perdida');
    toast.warning('Sin conexión', {
      description: 'Las órdenes se guardarán localmente',
      duration: 5000,
    });
  });
}
